/// <amd-module name="Test/TestResultsGrid/TestResultGridHelper" />
import * as Common from "Test/common-util/Common";
import { TestResultsGroupPivots } from "Test/common-util/Common";
import { TestResultsDetailsForGroup, TestSuite, WorkItemReference } from "Test/common-util/Contracts/Test";
import * as Resources from "Test/common-util/Resources";
import { publishEvent } from "Test/common-util/Telemetry";
import { equals, format } from "VSS/Core/Util/String";
import { IVssPageContext } from 'VSS/Platform/Context';
import { IVssContributionService } from "VSS/Platform/Contribution";

export class GroupByPivotHelper {
    public static getRunId(groupedResult: TestResultsDetailsForGroup, field: TestResultsGroupPivots): number {
        return field === Common.TestResultsGroupPivots.Group_By_Test_Run ? parseInt(groupedResult.groupByValue.id) : 0;
    }

    public static getPivotValueForGroupValue(groupedResult: TestResultsDetailsForGroup, field: TestResultsGroupPivots) {

        let groupByValue = "";
        switch (field) {
            case Common.TestResultsGroupPivots.Group_By_Test_Run:
                const testRun = groupedResult.groupByValue;
                groupByValue = testRun.name;
                break;

            case Common.TestResultsGroupPivots.Group_By_Test_Suite:
                const testSuite = groupedResult.groupByValue as TestSuite;
                groupByValue = (testSuite.id === 0) ? "" : testSuite.name;
                break;

            case Common.TestResultsGroupPivots.Group_By_Requirement:
                const workItem = groupedResult.groupByValue as WorkItemReference;
                const workItemId: number = parseInt(workItem.id);
                groupByValue = (workItemId === 0) ? "" : workItem.name;
                break;

            case Common.TestResultsGroupPivots.Group_By_Container:
            case Common.TestResultsGroupPivots.Group_By_Priority:
            case Common.TestResultsGroupPivots.Group_By_None:
            case Common.TestResultsGroupPivots.Group_By_Owner:
                groupByValue = groupedResult.groupByValue as string;
                break;
        }

        return GroupByPivotHelper.getPivotValue(field, groupByValue);
    }

    private static getPivotValue(pivot: string, pivotValue: string): string {
        let displayPivotValue = "";
        switch (pivot) {
            case Common.TestResultsGroupPivots.Group_By_Container:
                displayPivotValue = pivotValue;
                break;
            case Common.TestResultsGroupPivots.Group_By_Test_Run:
                displayPivotValue = pivotValue;
                break;
            case Common.TestResultsGroupPivots.Group_By_Requirement:
                displayPivotValue = equals(pivotValue, "") ? Resources.NotAssociatedText : pivotValue;
                break;
            case Common.TestResultsGroupPivots.Group_By_Test_Suite:
                displayPivotValue = pivotValue;
                break;
            case Common.TestResultsGroupPivots.Group_By_Priority:
                displayPivotValue = equals("255", pivotValue) ? "" : format("{0}: {1}", Resources.PriorityText, pivotValue);
                break;
            case Common.TestResultsGroupPivots.Group_By_Owner:
                displayPivotValue = pivotValue;
                break;
            case Common.TestResultsGroupPivots.Group_By_None:
                displayPivotValue = Resources.AllText;
                break;
            default:
                break;
        }

        if (equals(displayPivotValue, "")) {
            displayPivotValue = Resources.UnspecifiedText;
        }

        return displayPivotValue;
    }
}

export class Constants {
    public static pageSize: number = 100;
    public static OutcomeConfidenceField = "OutcomeConfidence";
    public static IsTestResultFlakyField = "IsTestResultFlaky";
    public static TestResultFlakyStateField = "TestResultFlakyState";
    public static filterchangedAction: string = "filterChangedAction";
    public static groupByChangedAction: string = "groupByChangedAction";
    public static loadMoreAction: string = "loadMoreAction";
    public static batchResultsSize: number = 100000;
    public static shallowResultsBatchSize: number = 10000;
}

export class FilterSettings {
    private static _registryData: {[key: string]: any};
    private static _megaResultsSizeRegistryName: string = "/Service/WebAccess/TestManagement/Settings/TotalResultsToFetch";
    private static _newShallowResultsBatchSizeRegistryName: string = "/Service/WebAccess/TestManagement/Settings/ResultsFetchBatchSize";

    private static async _getTheRegistrySettingsFromServer(pageContext: IVssPageContext):Promise<{[key: string]: any} | undefined> {
        return new Promise(async (resolve, reject) => {
            try {
                const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
                const registryData:{[key: string]: any} | undefined = contributionService.getData<{[key: string]: any}>("ms.vss-test-web.testmanagement-settings-provider");
                resolve(registryData);
            } catch (error) {
                reject(error);
            }
        });
    }

    private static _getRegistryValue(pageContext: IVssPageContext, key: string): string|undefined {
        if(!FilterSettings._registryData) {
            FilterSettings._getTheRegistrySettingsFromServer(pageContext).then((value:{[key: string]: any})=>{
                if(value) {
                    FilterSettings._registryData = value;
                } else {
                    FilterSettings._registryData = {};
                }
                return FilterSettings._getValue(key);
            }, (reason: any)=>{
                console.warn(`unable to get the registry from the data provider reason:${reason}`);
            });
        } else {
            return FilterSettings._getValue(key);
        }
    }

    private static _getValue(key: string): string|undefined {
        if(FilterSettings._registryData) {
            const registry = FilterSettings._registryData[key];
            return registry;
        }
    }

    public static getMegaResultsSize(pageContext: IVssPageContext): number {
        let value;
        try {
            value = FilterSettings._getRegistryValue(pageContext, FilterSettings._megaResultsSizeRegistryName);
        } catch (error) {
            // Not affecting the main flow, when there's error.
            console.error(`Couldn't get the registry setting ${FilterSettings._megaResultsSizeRegistryName}. Reason:${error}`);
            publishEvent(pageContext, null, "TestTab_RegistryFetch", {"RegistryName":FilterSettings._megaResultsSizeRegistryName, "Error":error});
        }

        return value ? +value : 350000;
    }

    public static getNewShallowResultsBatchSize(pageContext: IVssPageContext): number {
        let value;
        try {
            value = FilterSettings._getRegistryValue(pageContext, FilterSettings._newShallowResultsBatchSizeRegistryName);
        } catch (error) {
            // Not affecting the main flow, when there's error.
            console.error(`Couldn't get the registry setting ${FilterSettings._newShallowResultsBatchSizeRegistryName}. Reason:${error}`);
            publishEvent(pageContext, null, "TestTab_RegistryFetch", {"RegistryName":FilterSettings._newShallowResultsBatchSizeRegistryName, "Error":error});
        }

        return value ? +value : 20000;
    }
}